/**
 * Business Service
 * API calls for business search and retrieval
 */

import { apiClient } from './apiClient';

// --- Financial Year Records ---

export interface FinancialYearRecord {
  id: string;
  business_id: string;
  financial_year: string; // e.g. "2023-24"
  annual_turnover: number;
  net_profit: number;
  total_expenses: number;
  created_at: string;
  updated_at: string;
}

export interface CreateFinancialYearData {
  financial_year: string;
  annual_turnover: number;
  net_profit: number;
  total_expenses: number;
}

export interface UpdateFinancialYearData {
  annual_turnover?: number;
  net_profit?: number;
  total_expenses?: number;
}

// --- Compliance Filings ---

export type FilingStatus = 'up_to_date' | 'pending' | 'overdue' | 'not_applicable';
export type FilingType = 'gst' | 'mca' | 'itr';

export interface ComplianceFiling {
  id: string;
  business_id: string;
  filing_type: FilingType;
  status: FilingStatus;
  last_updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateComplianceData {
  filings: { filing_type: FilingType; status: FilingStatus }[];
}

// --- Business Documents ---

export interface BusinessDocument {
  id: string;
  business_id: string;
  file_name: string;
  document_type: string;
  s3_key: string;
  file_size_bytes: number;
  mime_type: string;
  uploaded_by: string;
  created_at: string;
}

// --- Badges ---

export interface BadgeResult {
  badge_id: string;
  earned: boolean;
  earned_at?: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  category: 'data_profile' | 'score_performance' | 'verification_trust' | 'ai_model' | 'other';
}

export interface BadgesResponse {
  badges: BadgeResult[];
  summary: { total: number; earned: number };
}

export interface UploadUrlMetadata {
  file_name: string;
  document_type: string;
  mime_type: string;
  file_size_bytes: number;
}

export interface UploadUrlResponse {
  upload_url: string;
  s3_key: string;
}

export interface ConfirmUploadData {
  file_name: string;
  document_type: string;
  s3_key: string;
  file_size_bytes: number;
  mime_type: string;
}

// --- Existing Interfaces ---

export interface BusinessListItem {
  id: string;
  pan: string;
  gstin: string | null;
  business_name: string;
  business_type: string;
  industry_sector: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  registered_address: string | null;
  annual_turnover: number | null;
  employee_count: number | null;
  registration_date: string;
  created_at: string;
  score: string | null;
  band: string | null;
  confidence: string | null;
  confidence_level: string | null;
  calculated_at: string | null;
}

export interface BusinessListResponse {
  businesses: BusinessListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface BusinessStats {
  totalBusinesses: number;
  averageScore: string;
  bandDistribution: {
    S: number;
    A: number;
    B: number;
    C: number;
    D: number;
    E: number;
    noScore: number;
  };
}

export interface BusinessSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  band?: string;
  minScore?: number;
  maxScore?: number;
}

export const businessService = {
  /**
   * Get list of businesses with pagination and filters
   */
  async getBusinesses(params: BusinessSearchParams = {}): Promise<BusinessListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.band) queryParams.append('band', params.band);
    if (params.minScore !== undefined) queryParams.append('minScore', params.minScore.toString());
    if (params.maxScore !== undefined) queryParams.append('maxScore', params.maxScore.toString());

    const response = await apiClient.get<BusinessListResponse>(
      `/businesses?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Get business by ID
   */
  async getBusinessById(id: string): Promise<BusinessListItem> {
    const response = await apiClient.get<BusinessListItem>(`/businesses/${id}`);
    return response.data;
  },

  /**
   * Get business by PAN
   */
  async getBusinessByPAN(pan: string): Promise<BusinessListItem> {
    const response = await apiClient.get<BusinessListItem>(`/businesses/pan/${pan}`);
    return response.data;
  },

  /**
   * Get business statistics
   */
  async getStats(): Promise<BusinessStats> {
    const response = await apiClient.get<BusinessStats>('/businesses/stats/summary');
    return response.data;
  },

  /**
   * Advanced search
   */
  async advancedSearch(params: {
    name?: string;
    pan?: string;
    gstin?: string;
    businessType?: string;
    industry?: string;
    minScore?: number;
    maxScore?: number;
    band?: string;
    minTurnover?: number;
    maxTurnover?: number;
    page?: number;
    limit?: number;
  }): Promise<BusinessListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await apiClient.get<BusinessListResponse>(
      `/businesses/search/advanced?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Get score history for a business
   */
  async getScoreHistory(businessId: string, limit: number = 12): Promise<ScoreHistoryResponse> {
    const response = await apiClient.get<ScoreHistoryResponse>(
      `/scores/${businessId}/history?limit=${limit}`
    );
    return response.data;
  },

  /**
   * Get red flags/alerts for a business
   */
  async getRedFlags(businessId: string, status?: 'active'): Promise<RedFlagsResponse> {
    const queryParams = status ? `?status=${status}` : '';
    const response = await apiClient.get<RedFlagsResponse>(
      `/redflags/${businessId}${queryParams}`
    );
    return response.data;
  },

  /**
   * Create a new business profile and link it to the current user
   */
  async createBusiness(data: {
    pan: string;
    business_name: string;
    business_type?: string;
    industry_sector?: string;
    contact_email?: string;
    contact_phone?: string;
    registered_address?: string;
    annual_turnover?: number;
    employee_count?: number;
    gstin?: string;
  }): Promise<BusinessListItem> {
    const response = await apiClient.post<BusinessListItem>('/businesses', data);
    return response.data;
  },

  /**
   * Update an existing business profile
   */
  async updateBusiness(id: string, data: Partial<{
    business_name: string;
    business_type: string;
    industry_sector: string;
    contact_email: string;
    contact_phone: string;
    registered_address: string;
    annual_turnover: number;
    employee_count: number;
    gstin: string;
    year_of_founding: number;
    number_of_locations: number;
  }>): Promise<BusinessListItem> {
    const response = await apiClient.put<BusinessListItem>(`/businesses/${id}`, data);
    return response.data;
  },

  // --- Financial Year Records ---

  /**
   * Get all financial year records for a business
   */
  async getFinancialYears(id: string): Promise<FinancialYearRecord[]> {
    const response = await apiClient.get<any>(`/businesses/${id}/financial-years`);
    // Backend returns { financial_years: [...] } — extract the array
    const data = response.data;
    return Array.isArray(data) ? data : (data.financial_years || data.records || []);
  },

  /**
   * Create a new financial year record for a business
   */
  async createFinancialYear(id: string, data: CreateFinancialYearData): Promise<FinancialYearRecord> {
    const response = await apiClient.post<any>(`/businesses/${id}/financial-years`, data);
    // Backend may wrap the record: { id, business_id, ..., badges } or { financial_year: {...} }
    const result = response.data;
    if (result && result.financial_year && typeof result.financial_year === 'object') {
      return result.financial_year;
    }
    return result;
  },

  /**
   * Update an existing financial year record
   */
  async updateFinancialYear(id: string, fyId: string, data: UpdateFinancialYearData): Promise<FinancialYearRecord> {
    const response = await apiClient.put<FinancialYearRecord>(`/businesses/${id}/financial-years/${fyId}`, data);
    return response.data;
  },

  /**
   * Delete a financial year record
   */
  async deleteFinancialYear(id: string, fyId: string): Promise<void> {
    await apiClient.delete(`/businesses/${id}/financial-years/${fyId}`);
  },

  // --- Compliance Filings ---

  /**
   * Get compliance filings for a business
   */
  async getCompliance(id: string): Promise<ComplianceFiling[]> {
    const response = await apiClient.get<any>(`/businesses/${id}/compliance`);
    const data = response.data;
    return Array.isArray(data) ? data : (data.filings || []);
  },

  /**
   * Update compliance filing statuses for a business
   */
  async updateCompliance(id: string, data: UpdateComplianceData): Promise<ComplianceFiling[]> {
    const response = await apiClient.put<any>(`/businesses/${id}/compliance`, data);
    const result = response.data;
    return Array.isArray(result) ? result : (result.filings || []);
  },

  // --- Badges ---

  /**
   * Get all badge evaluation results for a business
   */
  async getBadges(id: string): Promise<BadgesResponse> {
    const response = await apiClient.get<BadgesResponse>(`/businesses/${id}/badges`);
    return response.data;
  },

  // --- Documents ---

  /**
   * Get all documents for a business
   */
  async getDocuments(id: string): Promise<BusinessDocument[]> {
    const response = await apiClient.get<any>(`/businesses/${id}/documents`);
    const data = response.data;
    return Array.isArray(data) ? data : (data.documents || []);
  },

  /**
   * Get a presigned S3 upload URL for document upload
   */
  async getUploadUrl(id: string, metadata: UploadUrlMetadata): Promise<UploadUrlResponse> {
    const response = await apiClient.post<UploadUrlResponse>(`/businesses/${id}/documents/upload-url`, metadata);
    return response.data;
  },

  /**
   * Confirm a successful document upload and persist metadata
   */
  async confirmUpload(id: string, data: ConfirmUploadData): Promise<BusinessDocument> {
    const response = await apiClient.post<BusinessDocument>(`/businesses/${id}/documents`, data);
    return response.data;
  },

  /**
   * Delete a document
   */
  async deleteDocument(id: string, docId: string): Promise<void> {
    await apiClient.delete(`/businesses/${id}/documents/${docId}`);
  },
};

export interface ScoreHistoryItem {
  id: string;
  businessId: string;
  score: number;
  band: string;
  changeReason: string | null;
  changedBy: string | null;
  timestamp: string;
}

export interface ScoreHistoryResponse {
  businessId: string;
  businessName: string;
  history: ScoreHistoryItem[];
  trendAnalysis: {
    trend: string;
    volatility: number;
    averageChange: number;
    direction: string;
    dataPoints: number;
  };
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
  timestamp: string;
}

export interface RedFlagItem {
  id: string;
  category: string;
  condition: string;
  scoreImpact: number;
  actionRequired: string;
  detectedAt: string;
  resolvedAt: string | null;
  status: string;
}

export interface RedFlagsResponse {
  businessId: string;
  businessName: string;
  redFlags: RedFlagItem[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
  timestamp: string;
}
